import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { GraduationCap, FileCheck2, Landmark, BedDouble, ArrowUpRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { scrollToSection } from "@/lib/api";

const SERVICES = [
  {
    icon: GraduationCap,
    title: "Study Abroad Consulting",
    desc: "One-to-one counseling that maps your profile, budget and ambition to the right country, university and course.",
    points: ["University & course shortlisting", "SOP / LOR refinement", "Scholarship mapping"],
    slug: "study-abroad-consulting",
  },
  {
    icon: FileCheck2,
    title: "Study Visa Consultation",
    desc: "A meticulously prepared visa file, practiced interview rounds and honest financial-proof guidance.",
    points: ["File preparation & documentation", "Mock embassy interviews", "Financial proof guidance"],
    slug: "study-visa-consultation",
  },
  {
    icon: Landmark,
    title: "Education Loan Assistance",
    desc: "Understand secured and unsecured funding, explore bank and NBFC options, and prepare a stronger education-loan file.",
    points: ["Loans up to ₹1.5 Cr — subject to approval", "Secured & unsecured guidance", "Student & co-borrower document checklists"],
    slug: "education-loan-assistance",
  },
  {
    icon: BedDouble,
    title: "Accommodation Support",
    desc: "Safe, verified and budgeted housing sorted before you fly — plus the settling-in details nobody tells you about.",
    points: ["On-campus & verified off-campus", "Airport pickup coordination", "Pre-departure settling-in help"],
    slug: "accommodation-support",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="container-x py-20 lg:py-32">
      <SectionHeading
        overline="What we do"
        title={
          <>
            Four pillars. One promise —{" "}
            <span className="italic text-mint">you land prepared.</span>
          </>
        }
        desc="Everything a student needs between the first Google search and the first day of class, under one roof."
      />

      <div data-testid="services-grid" className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:gap-6">
        {SERVICES.map((s, i) => (
          <motion.div
            key={s.slug}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: (i % 2) * 0.1 }}
            data-testid={`service-card-${s.slug}`}
            className="group relative overflow-hidden rounded-[28px] border border-white/10 bg-surface p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-mint/40 hover:shadow-[0_24px_60px_rgba(0,0,0,0.45)] lg:p-10"
          >
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[radial-gradient(circle,rgba(74,222,128,0.12),transparent_70%)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
            <div className="flex items-start justify-between">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-mint/30 bg-ink text-mint transition-all duration-500 group-hover:bg-mint group-hover:text-ink">
                <s.icon className="h-6 w-6" strokeWidth={1.7} />
              </span>
              <span className="font-mono text-sm text-mist/60">0{i + 1}</span>
            </div>
            <h3 className="mt-7 font-display text-2xl font-normal tracking-tight sm:text-[1.7rem]">
              {s.title}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-mist sm:text-[15px]">{s.desc}</p>
            <ul className="mt-6 space-y-2.5">
              {s.points.map((p) => (
                <li key={p} className="flex items-center gap-3 text-sm text-frost/85">
                  <span className="h-1 w-1 rotate-45 bg-mint" />
                  {p}
                </li>
              ))}
            </ul>
            {s.slug === "education-loan-assistance" ? <Link to="/education-loan" data-testid={`service-cta-${s.slug}`} className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-mint transition-colors duration-300 hover:text-mintlight">Explore education loans<ArrowUpRight className="h-4 w-4" /></Link> : <button
              onClick={() => scrollToSection("#book")}
              data-testid={`service-cta-${s.slug}`}
              className="mt-8 flex items-center gap-2 text-sm font-semibold text-mint transition-colors duration-300 hover:text-mintlight"
            >
              Start with this
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </button>}
          </motion.div>
        ))}
      </div>
    </section>
  );
}
